import React from "react";
import CodeBlock from "../../common/code_block";
import HeaderWithTag from "../../common/header_with_tag";
import TrySendRemittance from "./try";
import ErrorCodesLink from "../../common/ErrorCodesLink";
import { Link } from "react-router-dom";
import { IRemittanceDto, PaymentMethod, SenderTypes } from "../../types/types";
import { HOST_API_URL } from "../../config";
import { PATH_PRIVATE } from "../../routes/paths";

const requestCode: IRemittanceDto = {
  requestUniqueId: "REQ123456789",
  paymentMethod: PaymentMethod.CASH,
  sender: {
    id: "SENDER001",
    name: "Abdirazak Ibrahim Abdirahman",
    phone: "252654249184",
    city: "Hargeisa",
    address: "2nd Floor, eDahab Building, Hargeisa Main Road",
    country: "Somalia",
    state: "Marodi-Jeh",
    postCode: "2112",
    nationality: "Somali",
    documentType: "Passport",
    documentNumber: "0145221564521",
    issueDate: "2023-09-01T00:00:00Z",
    expireDate: "2030-09-01T00:00:00Z",
    dateOfBirth: "1990-09-01T00:00:00Z",
    placeOfBirth: "Burao",
    gender: "Male",
    occupation: "Engineer",
    senderType: SenderTypes.INDIVIDUAL,
    sourceOfFund: "Salary"
  },
  receiver: {
    id: "REC001",
    name: "Jama Ali Warsame",
    destinationAgent: "TST",
    phone: "252659114455",
    address: "",
    nationality: "Somalia",
    receiverRelationship: "Friend"
  },
  bankDetails: {
    accountId: "HRGD123456",
    bankCode: "BANK001",
    bankSwift: "SWIFT001"
  },
  amount: 100.0,
  origRefNumber: "ORIG123456",
  currencyCode: "USD",
  purpose: "Education fees",
  narration: "Sending money to family",
  payoutAmount: 100.0
};

const responseCode = `{
  "statusCode": 200,
  "message": "Success",
  "errorCode": null,
  "data": {
    "transactionId": "XRT00020675"
  }
}`;

const failureResponseCode = `{
  "statusCode": 400,
  "message": "Insufficient balance to process this remittance",
  "errorCode": "DPI-3001",
  "data": null
}`;

const nameMismatchResponseCode = `{
  "statusCode": 400,
  "message": "Receiver name does not match the registered Edahab account name",
  "errorCode": "DPI-1027",
  "data": null
}`;

interface RequestField {
  name: string;
  type: string;
  description: React.ReactNode;
  required: string;
  min?: string;
  max?: string;
}

const REQUEST_FIELDS: RequestField[] = [
  {
    name: "requestUniqueId",
    type: "string",
    description:
      "Unique request identifier (idempotency key). Use a value that is unique across your systems, e.g. a UUID.",
    required: "Yes",
  },
  {
    name: "paymentMethod",
    type: "string",
    description: "Cash, Bank or Wallet. Bank requires bankDetails.",
    required: "Yes",
  },
  { name: "sender", type: "object", description: "Sender details", required: "Yes" },
  { name: "sender.id", type: "string", description: "Sender ID", required: "Yes" },
  { name: "sender.name", type: "string", description: "Sender's full name", required: "Yes" },
  {
    name: "sender.phone",
    type: "string",
    description: "Sender's phone. When supplied it must be 7 to 15 characters.",
    required: "No",
    min: "7",
    max: "15",
  },
  { name: "sender.city", type: "string", description: "Sender's city", required: "Yes" },
  { name: "sender.address", type: "string", description: "Sender's address", required: "Yes" },
  { name: "sender.country", type: "string", description: "Sender's country", required: "Yes" },
  { name: "sender.state", type: "string", description: "Sender's state", required: "No" },
  { name: "sender.postCode", type: "string", description: "Sender's postal code", required: "No" },
  { name: "sender.nationality", type: "string", description: "Sender's nationality", required: "No" },
  {
    name: "sender.documentType",
    type: "string",
    description: "Type of the sender's identity document; see Document Type in the Appendix",
    required: "Yes",
  },
  { name: "sender.documentNumber", type: "string", description: "Document number", required: "Yes" },
  {
    name: "sender.issueDate",
    type: "string",
    description: "Document issue date, ISO 8601 date-time",
    required: "Yes",
  },
  {
    name: "sender.expireDate",
    type: "string",
    description: "Document expiry date, ISO 8601 date-time",
    required: "Yes",
  },
  {
    name: "sender.dateOfBirth",
    type: "string",
    description: "Sender's date of birth, ISO 8601 date-time",
    required: "Yes",
  },
  { name: "sender.placeOfBirth", type: "string", description: "Place of birth", required: "No" },
  { name: "sender.gender", type: "string", description: "Gender", required: "No" },
  { name: "sender.occupation", type: "string", description: "Occupation", required: "No" },
  {
    name: "sender.senderType",
    type: "string",
    description: "Individual, Corporate or Charity",
    required: "Yes",
  },
  { name: "sender.sourceOfFund", type: "string", description: "Source of fund", required: "No" },
  {
    name: "sender.additionalField1",
    type: "string",
    description: "Spare field for corridor-specific data",
    required: "No",
  },
  {
    name: "sender.additionalField2",
    type: "string",
    description: "Spare field for corridor-specific data",
    required: "No",
  },
  { name: "receiver", type: "object", description: "Receiver details", required: "Yes" },
  {
    name: "receiver.id",
    type: "string",
    description: "Receiver's identity document number, when captured",
    required: "No",
  },
  {
    name: "receiver.name",
    type: "string",
    description: "Receiver's full name. For Edahab payouts see the name check below.",
    required: "Yes",
  },
  {
    name: "receiver.destinationAgent",
    type: "string",
    description: "Destination agent code, exactly 3 characters (see Get Agents)",
    required: "Yes",
    min: "3",
    max: "3",
  },
  {
    name: "receiver.phone",
    type: "string",
    description: "Receiver's phone, with country code for wallet payouts",
    required: "Yes",
    min: "7",
    max: "15",
  },
  { name: "receiver.address", type: "string", description: "Receiver's address", required: "No" },
  {
    name: "receiver.nationality",
    type: "string",
    description:
      "Receiver's nationality as a country name (e.g. Somalia). Required when commission is supplied; a destination agent may also require it.",
    required: "Conditional",
  },
  {
    name: "receiver.receiverRelationship",
    type: "string",
    description: "Relationship to sender",
    required: "No",
  },
  {
    name: "receiver.additionalField1",
    type: "string",
    description: "Spare field for corridor-specific data",
    required: "No",
  },
  {
    name: "receiver.additionalField2",
    type: "string",
    description: "Spare field for corridor-specific data",
    required: "No",
  },
  {
    name: "bankDetails",
    type: "object",
    description: "Bank details of the beneficiary",
    required: "When paymentMethod is Bank",
  },
  {
    name: "bankDetails.accountId",
    type: "string",
    description: "Beneficiary account number. Must be present in every bankDetails object.",
    required: "Yes, if bankDetails is sent",
  },
  { name: "bankDetails.bankCode", type: "string", description: "Bank code (see Get Banks By Country)", required: "No" },
  { name: "bankDetails.bankSwift", type: "string", description: "Bank SWIFT / code", required: "No" },
  {
    name: "amount",
    type: "decimal",
    description: "Sending amount. Must be greater than 0; no upper limit is enforced by the API.",
    required: "Yes",
    min: "> 0",
  },
  {
    name: "origRefNumber",
    type: "string",
    description: "Your own end-to-end reference, stored against the transaction. Keep it unique.",
    required: "Yes",
  },
  { name: "currencyCode", type: "string", description: "Currency code", required: "No" },
  { name: "purpose", type: "string", description: "Purpose of remittance. Free text.", required: "No" },
  { name: "narration", type: "string", description: "Narration", required: "No" },
  {
    name: "payoutAmount",
    type: "decimal",
    description: (
      <>
        Amount disbursed to the beneficiary. Optional in the schema, but populate it
        whenever the beneficiary payout differs from <code>amount</code> &mdash; there is
        no other field for this value.
      </>
    ),
    required: "No",
  },
  {
    name: "bankAccount",
    type: "string",
    description: (
      <>
        Not used for payout routing; send the account in{" "}
        <code>bankDetails.accountId</code>.
      </>
    ),
    required: "No",
  },
  {
    name: "commission",
    type: "decimal",
    description: (
      <>
        Commission amount. When supplied it is checked against the commission table for
        the corridor (DPI-3003) and <code>receiver.nationality</code> becomes required.
      </>
    ),
    required: "No",
  },
];

interface FieldCode {
  field: string;
  code: string;
  when: string;
}

const FIELD_CODES: FieldCode[] = [
  { field: "requestUniqueId", code: "DPI-1001", when: "Missing or blank" },
  { field: "origRefNumber", code: "DPI-1002", when: "Missing or blank" },
  { field: "amount", code: "DPI-1004 / DPI-1006", when: "Zero, negative or missing / not a number" },
  { field: "paymentMethod", code: "DPI-1006", when: "Unrecognised value" },
  { field: "sender", code: "DPI-1050", when: "Sent as null" },
  { field: "sender.id", code: "DPI-1010", when: "Missing or blank" },
  { field: "sender.name", code: "DPI-1011", when: "Missing or blank" },
  {
    field: "sender.phone",
    code: "DPI-1013 / DPI-1012",
    when: "Supplied but not 7 to 15 characters / missing while the destination agent requires it",
  },
  { field: "sender.documentType", code: "DPI-1014 / DPI-1015", when: "Missing / not an accepted type" },
  { field: "sender.city", code: "DPI-1016", when: "Missing or blank" },
  { field: "sender.address", code: "DPI-1017", when: "Missing or blank" },
  { field: "sender.country", code: "DPI-1018", when: "Missing or blank" },
  { field: "sender.documentNumber", code: "DPI-1019", when: "Missing or blank" },
  {
    field: "sender.issueDate, sender.expireDate, sender.dateOfBirth",
    code: "DPI-1006",
    when: "Not a valid date-time",
  },
  { field: "receiver", code: "DPI-1051", when: "Sent as null" },
  { field: "receiver.id", code: "DPI-1020", when: "Missing while the destination agent requires it" },
  {
    field: "receiver.name",
    code: "DPI-1021 / DPI-1027",
    when: "Missing or blank / does not match the registered Edahab wallet name",
  },
  {
    field: "receiver.phone",
    code: "DPI-1022 / DPI-1023 / DPI-1028",
    when: "Missing / not 7 to 15 characters / Edahab wallet could not be verified",
  },
  {
    field: "receiver.nationality",
    code: "DPI-1024",
    when: "Missing while commission is supplied, or while the destination agent requires it",
  },
  {
    field: "receiver.destinationAgent",
    code: "DPI-1025 / DPI-1026",
    when: "Missing / not exactly 3 characters",
  },
  { field: "bankDetails", code: "DPI-1030", when: "Missing while paymentMethod is Bank" },
  {
    field: "bankDetails.accountId",
    code: "DPI-1031 / DPI-1000",
    when: "Blank while paymentMethod is Bank / key absent from the bankDetails object",
  },
];

function SendRemittance() {
  return (
    <div className="page">
      <div className="app-content main-content mt-0">
        <div className="side-app">
          <div className="main-container container-fluid">
            <div className="page-header">
              <div>
                <h1 className="page-title">Send Remittance</h1>
              </div>
              <div className="ms-auto pageheader-btn">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="javascript:void(0);">Docs</a>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Send Remittance
                  </li>
                </ol>
              </div>
            </div>

            <div className="card overflow-hidden">
              <div className="row">
                <div className="col-md-7" style={{ position: "relative" }}>
                  <div className="card-body border-end p-0">
                    <div className="p-5">
                      <HeaderWithTag
                        headerText="Initiate a remittance"
                        headerTag="description"
                      />
                      <p className="text-12">
                        <span className="badge badge-sm bg-blue text-white me-2">
                          POST
                        </span>
                        {HOST_API_URL}/Remittances/SendRemittance
                      </p>
                      <p
                        className="text-muted text-14"
                        style={{ textAlign: "justify" }}
                      >
                        Submit a new remittance transaction. Provide full sender and receiver details,
                        amounts, and required documentary information. A successful response returns
                        a transaction identifier you can use to query status or perform follow‑up actions.
                        Validate the destination account or wallet with{" "}
                        <Link to={PATH_PRIVATE.ValidateAccount}>Validate Account</Link> before
                        sending.
                      </p>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Request"
                          headerTag="request"
                        />
                        <div className="table-responsive">
                          <table className="table text-nowrap text-md-nowrap table-bordered">
                            <thead>
                              <tr>
                                <th>Parameter</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                                <th>Min</th>
                                <th>Max</th>
                              </tr>
                            </thead>
                            <tbody>
                              {REQUEST_FIELDS.map((field) => (
                                <tr key={field.name}>
                                  <td>{field.name}</td>
                                  <td>{field.type}</td>
                                  <td>{field.description}</td>
                                  <td>{field.required}</td>
                                  <td>{field.min ?? ""}</td>
                                  <td>{field.max ?? ""}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="text-muted text-14 mb-0">
                          Property names are matched case-insensitively. Min and Max are
                          only shown where the API enforces a length; a required string
                          must not be empty or whitespace. Send an optional field as an
                          empty string or omit the key; do not send <code>null</code> for
                          string fields.
                        </p>
                      </div>

                      <div className="my-5">
                        <CodeBlock
                          code={JSON.stringify(requestCode, null, 2)}
                          codeBlackText="Request"
                          codeLanguage="json"
                        />
                      </div>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Response"
                          headerTag="response"
                        />
                        <div className="table-responsive">
                          <table className="table text-nowrap text-md-nowrap table-bordered">
                            <thead>
                              <tr>
                                <th>Parameters</th>
                                <th>Type</th>
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>statusCode</td>
                                <td>integer</td>
                                <td>200 on success, otherwise the failure status</td>
                              </tr>
                              <tr>
                                <td>message</td>
                                <td>string</td>
                                <td>"Success", or the reason for the failure</td>
                              </tr>
                              <tr>
                                <td>errorCode</td>
                                <td>string</td>
                                <td>null on success; a stable DPI-xxxx code on failure</td>
                              </tr>
                              <tr>
                                <td>data.transactionId</td>
                                <td>string</td>
                                <td>Transaction ID (TTNO) of the submitted remittance</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <p className="text-muted text-14 mb-0">
                          Treat a 200 whose <code>transactionId</code> is empty as
                          unconfirmed and query Get Transaction Details with your{" "}
                          <code>origRefNumber</code>. If the back office refuses the
                          transaction after every check has passed, the response is HTTP
                          400 with the back-office reason in <code>message</code>,{" "}
                          <code>errorCode</code> is DPI-1000 unless the reason matches a
                          catalogued message, and <code>data</code> may still carry a{" "}
                          <code>transactionId</code>. The <code>requestUniqueId</code> is
                          not released in that case.
                        </p>
                      </div>

                      <div className="my-5">
                        <CodeBlock
                          code={responseCode}
                          codeBlackText="Response Example"
                          codeLanguage="json"
                        />
                      </div>

                      <div className="my-5">
                        <CodeBlock
                          code={failureResponseCode}
                          codeBlackText="Failure Example"
                          codeLanguage="json"
                        />
                      </div>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="How a request is processed"
                          headerTag="processing"
                        />
                        <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                          Checks run in the order below and the first failure is returned.
                          Up to and including the commission step, a rejection releases
                          the <code>requestUniqueId</code>, so the corrected request may
                          reuse it. From transaction creation onward the id stays
                          reserved for 10 minutes.
                        </p>
                        <ol className="text-14" style={{ marginLeft: 10 }}>
                          <li>
                            <strong>Model binding and required fields.</strong> DPI-1006
                            for a malformed value, otherwise the field codes listed below.
                            Several messages are joined with <code>, </code> and{" "}
                            <code>errorCode</code> reflects the first. The id is not
                            registered yet.
                          </li>
                          <li>
                            <strong>Duplicate check.</strong> DPI-5001 if the{" "}
                            <code>requestUniqueId</code> was seen in the last 10 minutes.
                          </li>
                          <li>
                            <strong>Destination-agent required fields.</strong>{" "}
                            <code>"&lt;Field&gt; is required"</code> messages joined with{" "}
                            <code>; </code>; <code>errorCode</code> is that of the first,
                            or DPI-1000 if it is not a catalogued message.
                          </li>
                          <li>
                            <strong>Field rules.</strong> DPI-1026, DPI-1013, DPI-1023,
                            DPI-1004, DPI-1015, also joined with <code>; </code>.
                          </li>
                          <li>
                            <strong>Bank details</strong>, only when{" "}
                            <code>paymentMethod</code> is Bank. DPI-1030, DPI-1031.
                          </li>
                          <li>
                            <strong>Partner account.</strong> DPI-6002 if the authenticated
                            user cannot be resolved.
                          </li>
                          <li>
                            <strong>Balance and credit limit</strong>, where a credit limit
                            applies to your account. DPI-3001, DPI-3002.
                          </li>
                          <li>
                            <strong>Edahab receiver name check</strong>, where enabled for
                            your account. DPI-1027, DPI-1028 (see below).
                          </li>
                          <li>
                            <strong>Commission</strong>, only when <code>commission</code>{" "}
                            is supplied. DPI-1024 if <code>receiver.nationality</code> is
                            missing, DPI-3003 if the value differs from the computed
                            commission, DPI-3004 and DPI-3005 (both returned with HTTP
                            500) if the corridor or commission cannot be determined.
                          </li>
                          <li>
                            <strong>Transaction creation.</strong> 200 with{" "}
                            <code>data.transactionId</code>, a back-office rejection as
                            described under Response, or DPI-9001 on an unexpected error.
                          </li>
                        </ol>
                      </div>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Edahab wallet payouts: receiver name check"
                          headerTag="edahabNameCheck"
                        />
                        <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                          For partner accounts where this control is enabled, a payout to
                          an Edahab wallet (<code>receiver.destinationAgent</code> ={" "}
                          <code>EDH</code>) is accepted only if <code>receiver.name</code>{" "}
                          matches the name the wallet is registered under. The wallet is
                          looked up by <code>receiver.phone</code>, so send the phone with
                          the country code (e.g. <code>252654249184</code>).
                        </p>
                        <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                          The comparison is tolerant: the order of the name parts does not
                          matter, a shortened name (for example without a middle name) is
                          accepted, and common Somali and Arabic spellings of the same
                          name are treated as equal. At least two parts of the name must
                          correspond, or the single part when the wallet is registered
                          under one name.
                        </p>
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th className="text-nowrap">Code</th>
                                <th className="text-nowrap">Meaning</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="text-nowrap">
                                  <strong>DPI-1027</strong>
                                </td>
                                <td className="text-nowrap">Name does not belong to that wallet</td>
                                <td>
                                  Confirm the receiver's details and resubmit. The{" "}
                                  <code>requestUniqueId</code> is released, so you may
                                  reuse it.
                                </td>
                              </tr>
                              <tr>
                                <td className="text-nowrap">
                                  <strong>DPI-1028</strong>
                                </td>
                                <td className="text-nowrap">Wallet could not be checked</td>
                                <td>
                                  The number may not be a registered Edahab wallet, or the
                                  lookup was temporarily unavailable. Confirm the number
                                  with Validate Account (entity EDAHAB, type wallet) and
                                  retry after a short delay.
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="my-4">
                          <CodeBlock
                            code={nameMismatchResponseCode}
                            codeBlackText="Name Mismatch Example"
                            codeLanguage="json"
                          />
                        </div>
                      </div>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Field Validation Codes"
                          headerTag="fieldCodes"
                        />
                        <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                          Each check maps to its own code, so you can point a failed
                          submission at the exact field without parsing the message text.
                        </p>
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>Field</th>
                                <th className="text-nowrap">Code</th>
                                <th>When</th>
                              </tr>
                            </thead>
                            <tbody>
                              {FIELD_CODES.map((row) => (
                                <tr key={row.field}>
                                  <td>{row.field}</td>
                                  <td className="text-nowrap">{row.code}</td>
                                  <td>{row.when}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="alert alert-info text-14">
                          <strong>Malformed values fail earlier.</strong> An unrecognised
                          enum value, a non-numeric amount or an unparseable date is
                          rejected during model binding with <code>DPI-1006</code>, before
                          any of the checks above run.
                        </div>

                        <div className="alert alert-warning text-14 mb-0">
                          <strong>Destination agents can require extra fields.</strong> The
                          value you send in <code>receiver.destinationAgent</code>
                          determines an additional, agent-specific set of required fields.
                          You may therefore receive
                          <code> "&lt;Field&gt; is required"</code> errors for fields marked
                          optional in the table above. Several such errors are joined into
                          one <code>message</code>, separated by <code>;</code>, and
                          <code> errorCode</code> reflects the first of them, or is
                          <code> DPI-1000</code> when that message is not a catalogued one.
                        </div>
                      </div>

                      <ErrorCodesLink />
                    </div>
                  </div>
                </div>
                <div className="col-md-5 pt-5">
                  <h5 className="card-title text-primary">
                    Code Samples
                  </h5>

                  <TrySendRemittance
                    codeBlackTitle="Request Example"
                    sendRemittanceObject={requestCode}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SendRemittance;
